<template>
  <div class="module">
    <Spinner v-if="getLoading"></Spinner>

    <transition name="slide-fade">
      <div class="charts panel" v-if="!getLoading">
        <div class="filters">
          <a
            class="filter"
            @click.prevent="filterUpdate(7)"
            href="#"
            :class="{ active: dayFilter === 7 && !customSearch }"
            >Week</a
          >

          <a
            class="filter"
            @click.prevent="filterUpdate(30)"
            href="#"
            :class="{ active: dayFilter === 30 && !customSearch }"
            >Month</a
          >

          <a
            class="filter"
            href="#"
            @click="toggleCustomDateFilter"
            :class="{ active: customSearch }"
            >Custom</a
          >
        </div>

        <!-- Custom date filter -->
        <form
          class="custom-filter"
          v-if="showCustomDateFilter"
          @submit.prevent="filterCustomDates(false)"
        >
          <label for="custom-start-date">
            <span>Start date</span>
            <input
              type="date"
              name="custom-start-date"
              id="custom-start-date"
              v-model="customStartDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <label for="custom-end-date">
            <span>End date</span>
            <input
              type="date"
              name="custom-end-date"
              id="custom-end-date"
              v-model="customEndDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <input class="button button--standard" type="submit" value="Go" />
        </form>

        <div class="date-change" v-if="!customSearch">
          <a @click.prevent="previousDates" class="previous-dates" href="#">
            <img
              :src="require('@/assets/img/arrow.svg')"
              alt="previous date set"
            />
          </a>

          <span
            >{{ formatDate(filterStartDate) }} -
            {{ formatDate(filterEndDate) }}</span
          >

          <a
            @click.prevent="nextDates"
            class="next-dates"
            href="#"
            v-if="!isFilterEndToday"
          >
            <img :src="require('@/assets/img/arrow.svg')" alt="next date set" />
          </a>
        </div>

        <component
          :is="currentModuleCharts"
          :existingEntries="existingEntries"
          v-if="!getLoading"
          :key="chartKey"
        ></component>
      </div>
    </transition>

    <transition name="slide-fade" v-if="!getLoading">
      <component :is="currentModuleGoals"></component>
    </transition>

    <transition name="slide-fade">
      <div class="entries" v-if="!getLoading">
        <details
          v-for="(day, index) in days"
          :key="index"
          :class="{ entry: day.entry }"
        >
          <summary :id="'accordion-' + index" @click="openAccordion">
            {{ formatDate(day.date) }}
            <span class="need-log" v-if="!day.entry">Log your steps</span>
            <Arrow class="arrow" :color="day.entry ? '#000' : '#fff'"></Arrow>
          </summary>

          <div class="details-content">
            <!-- Bring in the stats and operations for this module. -->
            <div v-if="!day.showForm">
              <div class="operations" v-if="!day.delete">
                <a
                  class="button button--standard"
                  @click.prevent="editEntry(day)"
                  href="#"
                  >edit</a
                >
                <a
                  class="button button--danger"
                  @click.prevent="toggleDelete(day)"
                  href="#"
                  >delete</a
                >
              </div>

              <div class="delete-confirm" v-if="day.delete">
                <p>
                  Are you sure you want to delete the entry for
                  {{ formatDate(day.date) }}?
                </p>
                <button
                  class="button button--standard"
                  @click="deleteEntry(day)"
                >
                  Confirm
                </button>
                <button
                  class="button button--danger"
                  @click="toggleDelete(day)"
                >
                  Cancel
                </button>
              </div>

              <div class="statistics-wrapper" v-if="!day.delete">
                <header class="stat-heading">
                  Steps statistics for
                  {{ formatDate(day.date) }}
                </header>
                <component
                  :is="currentModuleStats"
                  :existingEntry="day.entry"
                ></component>
              </div>
            </div>

            <!-- Load in the form for the module if there isn't an entry -->
            <component
              v-if="day.showForm"
              :is="currentModuleForm"
              @form-submit="formSubmit"
              @cancel-edit="cancelEdit"
              :existingEntry="day.entry"
              :day="day"
            ></component>
          </div>
        </details>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";

import { subDays } from "date-fns";

import Spinner from "@/components/Spinner.vue";
import Arrow from "@/components/svg/Arrow.vue";

import firebase, { firestore } from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

import Utility from "@/mixins/utility";

export default {
  name: "Steps",

  components: { Spinner, Arrow },

  mixins: [Utility],

  filters: {
    capitalize: function(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  async created() {
    // Check for user.
    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        // Set the user id.
        this.setUID(user.uid);

        // Track module access for research analytics
        const { trackModuleAccess } = await import("@/utils/analytics");
        trackModuleAccess('behavior', 'steps', user.uid);

        // Grab pain entries.
        await this.firestoreGetPainEntries(
          this.filterStartDate,
          this.filterEndDate
        );

        // Set loading to true.
        this.modifyLoading(true);

        // Grabs entries and days.
        this.updateDays(7, this.filterStartDate, this.filterEndDate, "steps");

        // Set logged in to true.
        this.modifyLoggedIn(true);

        // Set the page title.
        this.changePageTitle("steps");

        // Set page text.
        this.changePageIntroText("");

        // Disable loader.
        this.disableLoader(1000);
      } else {
        // Send the user to the auth page if they aren't logged in.
        this.$router.push("/auth");
      }
    });
  },

  computed: {
    ...mapGetters(["getLoading", "getAnalytics"]),

    // Imports the matching module form fields.
    currentModuleForm() {
      return () => import(`../components/steps/Form.vue`);
    },

    // Imports the matching charts.
    currentModuleCharts() {
      return () => import(`../components/steps/Charts.vue`);
    },

    // Imports the matching stats.
    currentModuleStats() {
      return () => import(`../components/steps/Stats.vue`);
    },

    currentModuleGoals() {
      return () => import(`../components/steps/Goals.vue`);
    },
  },

  methods: {
    ...mapMutations([
      "changePageTitle",
      "changePageIntroText",
      "modifyLoggedIn",
    ]),

    // Handle form submit.
    formSubmit(data, day) {
      this.addNewEntry = false;

      // Create a new entry.
      this.createLogEntry("steps", data, day.date);
    },

    cancelEdit(day) {
      day.showForm = false;
    },

    editEntry(day) {
      day.showForm = true;
    },

    toggleDelete(day) {
      day.delete = !day.delete;
    },

    deleteEntry(day) {
      this.deleteLogEntry("steps", day.date);
      day.delete = false;
    },

    openAccordion() {
      // This method can be used for analytics or other functionality
    },
  },
};
</script>

<style scoped>
/* Styles will be inherited from the existing module styles */
</style>
